import { createSelector } from 'reselect';

import { DEFAULT_PROJECT_NAME } from 'modules/projects/const';
import { JWT } from 'domains/jwtToken/types';
import { RootState } from 'store';
import { createNotifyingQueryFn } from 'store/utils/createNotifyingQueryFn';
import { createQuerySelectors } from 'store/utils/createQuerySelectors';
import { selectUserEndpointToken } from 'modules/account/store/selectors';
import { web3Api } from 'store/queries';

import { IFetchRawJWTsParams, fetchRawJWTs } from './fetchRawJWTs';
import { PRIMARY_JWT_INDEX } from '../const';
import { decodeJWTs } from '../utils/decodeJWTs';
import { isPrimaryJWTIndex } from '../utils/isPrimaryJWTIndex';

export interface IFetchJWTsParams extends IFetchRawJWTsParams {
  forceFetchInternal?: boolean;
}

// The endpoint name is listed in endpointsSerializedByParams constant
// in packages/protocol/src/store/queries/index.ts file.
// If the name has changed it should be reflected there as well.
export const {
  endpoints: { fetchJWTs },
  useFetchJWTsQuery,
  useLazyFetchJWTsQuery,
} = web3Api.injectEndpoints({
  endpoints: build => ({
    fetchJWTs: build.query<JWT[], IFetchJWTsParams>({
      queryFn: createNotifyingQueryFn(
        async ({ forceFetchInternal, ...params }, { dispatch, getState }) => {
          const jwts = await dispatch(
            fetchRawJWTs.initiate(params, { forceRefetch: forceFetchInternal }),
          ).unwrap();

          const state = getState() as RootState;

          const primaryUserEndpointToken = selectUserEndpointToken(state);

          const data = await decodeJWTs({
            hasGroupSelected: Boolean(params.group),
            jwts,
            primaryUserEndpointToken,
          });

          return { data };
        },
      ),
    }),
  }),
  overrideExisting: true,
});

const fetchJWTsFallback: JWT[] = [];

const {
  selectDataWithFallbackCachedByParams,
  selectLoadingCachedByParams: selectJWTsLoading,
  selectStateCachedByParams: selectJWTsState,
} = createQuerySelectors({ endpoint: fetchJWTs, fallback: fetchJWTsFallback });

export { selectJWTsLoading, selectJWTsState };

export const selectJWTs = createSelector(
  selectDataWithFallbackCachedByParams,
  selectUserEndpointToken,
  (jwts, userEndpointToken = '') => {
    const sortedByIndex = [...jwts].sort((a, b) => a.index - b.index);

    const primaryJWT = sortedByIndex.find(jwt => isPrimaryJWTIndex(jwt.index));

    if (!primaryJWT) {
      const defaultPrimaryJWT: JWT = {
        description: '',
        index: PRIMARY_JWT_INDEX,
        name: DEFAULT_PROJECT_NAME,
        userEndpointToken,
      };

      return [defaultPrimaryJWT, ...sortedByIndex];
    }

    if (!primaryJWT.name) {
      const primaryJWTWithDefaultName: NonNullable<typeof primaryJWT> = {
        ...primaryJWT,
        name: DEFAULT_PROJECT_NAME,
      };

      return [primaryJWTWithDefaultName, ...sortedByIndex.slice(1)];
    }

    return sortedByIndex;
  },
);
